// Angular
import { Injectable, Inject, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
// State
import { Store } from '@ngxs/store';
// Models
import { UserContextModel } from '../auth/user-context.model';
import {
	AppHeaderData,
	AppServiceModel,
	AppSwitcherMenu,
	ChangeRoutOnErrorData,
	RedirectionData,
	RoleByPermission
} from '../model/app-functions.model';
import { ErrorCodeModel } from '../error-track/model/error-code.model';

/**
 * Constant with the injection token, needed for the dynamic perApp service injection
 */
export const APP_SERVICE = new InjectionToken<any>('app.service');

/**
 * AppService load factory
 *
 * @param data
 * @returns
 */
export function loadAppConfig(specificAppService: AppServiceModel): AppService {
	const service = new AppService(specificAppService);

	return service;
}

@Injectable({
	providedIn: 'root'
})
export class AppService {
	public appServiceWrapper: AppServiceModel;

	constructor(@Inject(APP_SERVICE) perAppService: AppServiceModel) {
		this.appServiceWrapper = perAppService;
	}

	/**
	 * header.component
	 * Given the user permissions, generate the App header & module
	 *
	 * @param permissions
	 * @returns
	 */
	getAppHeader(permissions: any): AppHeaderData {
		const data = this.appServiceWrapper.getAppHeader(permissions);

		return data;
	}

	/**
	 * header.component
	 * Given the user permissions, generate the AppSwitcher
	 *
	 * @param user
	 * @param currentModule
	 * @returns
	 */
	getAppSwitcherMenu(user: any, currentModule: string): AppSwitcherMenu {
		return this.appServiceWrapper.getAppSwitcher(user, currentModule);
	}

	/**
	 * header.component
	 * Given the module path, check if it can show the return to inbox.
	 *
	 * @param module
	 * @returns
	 */
	getHeaderShowInboxButton(module: string): boolean {
		return this.appServiceWrapper.getHeaderShowInboxButton(module);
	}

	/**
	 * header.component
	 * Given the user permissions, check if it can show the appSwitcher
	 *
	 * @param permissions
	 * @returns
	 */
	getCanAccessMultipleApps(permissions: any): boolean {
		const canAccess = this.appServiceWrapper.getCanAccessMultipleApps(permissions);

		return canAccess;
	}

	/**
	 * header.component
	 * Get the array of special apps that will
	 *
	 * @returns
	 */
	getIgnoreFormTypeApps(): Array<any> {
		return this.appServiceWrapper.headerIgnoreFormTypeApps;
	}

	/**
	 * role.service
	 * Get the ROLE given the permissions array
	 *
	 * @param permissions
	 * @returns
	 */
	getRoleByPermission(permissions: any): RoleByPermission {
		const role = this.appServiceWrapper.getRoleByPermission(permissions);

		return role;
	}

	/**
	 * role.service
	 * Get the ROLE given the permissions array & current url
	 *
	 * @param permissions
	 * @param url
	 * @returns
	 */
	getRoleByPermissionAndUrl(permissions: any, url: string): RoleByPermission {
		const role = this.appServiceWrapper.getRoleByPermissionAndUrl(permissions, url);

		return role;
	}

	/**
	 * role.service
	 * Get the defautl PATH given the permissions
	 *
	 * @param permissions
	 */
	getDefaultPathByPermissions(permissions: any): string {
		const path = this.appServiceWrapper.getDefaultPathByPermissions(permissions);

		return path;
	}

	/**
	 * role.service
	 * Get the Default PATH given the url and the Module
	 *
	 * @param permissions
	 */
	getDefaultPath(url: string, currentModule: string): any {
		return this.appServiceWrapper.getDefaultPath(url, currentModule);
	}

	/**
	 * Get the user data and menu from an endpoint or JWT
	 *
	 * @param userInfo The payload from the JWT decode
	 * @param token The JWT token
	 */
	getUserData(userInfo: any, token: string): Observable<UserContextModel> {
		return this.appServiceWrapper.getUserData(userInfo, token);
	}

	/**
	 * Update the user data using the token
	 *
	 * @param userInfo The payload from the JWT decode
	 * @param token The JWT token
	 */
	getUserDataFromToken(userInfo: any, token: string): Observable<UserContextModel> {
		return this.appServiceWrapper.getUserDataFromToken(userInfo, token);
	}

	/**
	 * role.service
	 * Get redirectionData given the url and role
	 *
	 * @param url
	 * @param role
	 * @returns
	 */
	getRedirectionData(url: string, role: any): RedirectionData {
		const data = this.appServiceWrapper.getRedirectionData(url, role);

		return data;
	}

	/**
	 * role.service
	 * Skip CanActivate from auth.guard
	 *
	 * @param currentRoute
	 * @param role
	 * @returns TRUE|FALSE
	 */
	getSkipCanActivateGuard(currentRoute: any, role: any): boolean {
		const skip = this.appServiceWrapper.getSkipCanActivateGuard(currentRoute, role);

		return skip;
	}

	/**
	 * order-event-notification.component
	 * Get the module main url to use as base link for the notifications
	 *
	 * @param modulePath
	 * @returns
	 */
	getMainModuleUrl(modulePath: string): string {
		const mainUrl = this.appServiceWrapper.getMainModuleUrl(modulePath);

		return mainUrl;
	}

	/**
	 * IntelligenceEndpointResolver
	 * Get the appName to use for the telemetry connection
	 *
	 * @param url
	 */
	getTelemetryAppName(url: string): string {
		const appName = this.appServiceWrapper.getTelemetryAppName(url);

		return appName;
	}

	/**
	 * http-request.interceptor
	 * Verify if its needed a route change on error
	 *
	 * @param module
	 * @param errorMessage
	 * @returns
	 */
	getChangeRouteOnError(module: string, errorMessage: string): ChangeRoutOnErrorData {
		const result = this.appServiceWrapper.getChangeRouteOnError(module, errorMessage);

		return result;
	}

	/**
	 * http-request.interceptor
	 * Get the regex patters to skip the notification on error
	 *
	 * @returns Regex pattern Array
	 */
	getToasterExceptions(): Array<any> {
		const skips = this.appServiceWrapper.httpInterceptorExceptions.toaster || [];

		return skips;
	}

	/**
	 * http-request.interceptor
	 * Get the regex patters to skip the notification on error
	 *
	 * @returns Regex pattern Array
	 */
	getHandledByOtherExceptions(): Array<any> {
		const skips = this.appServiceWrapper.httpInterceptorExceptions.handledByOtherInterceptor || [];

		return skips;
	}

	/**
	 * http-request.interceptor
	 * Get the regex patters to skip the notification on error
	 *
	 * @returns Regex pattern Array
	 */
	getSwallowValidErrors(): Array<any> {
		const skips = this.appServiceWrapper.httpInterceptorExceptions.swallowValidErrors || [];

		return skips;
	}

	/**
	 * signal-r.service
	 * Get the signalR connection url given the user role
	 *
	 * @param module
	 * @param user
	 * @param config
	 * @returns
	 */
	getSignalRUrl(module: string, currentUrl: string, user: any, config: any): string {
		const url = this.appServiceWrapper.getSignalRUrl(module, currentUrl, user, config);

		return url;
	}

	/**
	 * signal-r.service
	 * Get the array of special apps that will invoke a hub function function
	 *
	 * @returns
	 */
	getSignalRConnectionIdApps(): Array<any> {
		return this.appServiceWrapper.signalRConnectionIdApps;
	}

	/**
	 * signal-r.service
	 * Function to check if the signalR event can be stored in the state.
	 * OPTIONAL IMPLEMENTATION:
	 * If the app requires special logic (invisionUX), you have to implement it in the perApp service.
	 * If not, the default value returned it is true;
	 *
	 * @param data An Object with the function's data required to process
	 * @returns boolean
	 */
	canSignalRStoreEvents(data: any = {}): boolean {
		return this.appServiceWrapper.canSignalRStoreEvents(data);
	}

	/**
	 * clear-state.plugin
	 * Get the default state to update while moving between apps
	 *
	 * @param module
	 */
	getModuleBaseState(module: string): any {
		const moduleState = this.appServiceWrapper.getModuleBaseState(module);

		return moduleState;
	}

	/**
	 * Return the List of Signal Exception Events that this app needs to manage
	 */
	getSignalExceptionEvents(): any[] {
		return this.appServiceWrapper.getSignalExceptionEvents();
	}

	isSMSEnabled(store: Store): boolean {
		return this.appServiceWrapper.isSMSEnabled(store);
	}

	/**
	 * Get the Main API Configuration where the utils exists that can be used to load external configuration
	 */
	public getSecretConfigAPI(): string {
		return this.appServiceWrapper.getSecretConfigAPI();
	}

	/**
	 * Get the Main API Secret Config Route
	 */
	public getSecretConfigRoute(): string {
		return this.appServiceWrapper.getSecretConfigRoute();
	}
}
